import {
  APP_PATHS,
  getAdminActionPlanDetailPath,
  getAdminObligationPath,
  getAdminObligationReportPath,
} from '@/shared/constants/routes';
import {
  getNoticeConsultationDocumentUrl,
  getNoticesListUrl,
} from '@/shared/utils/urlUtils';
import { getHomeActionPlanCtaHref } from './homeActionPlanCtaHref';
import {
  buildModeratorAssignedNotificationContext,
  buildModeratorObligationNotificationLink,
  buildProfileObligationNotificationLink,
} from './obligationNotificationLinks';
import {
  buildProfileObligationProposalFocusHref,
  buildProfileObligationProposalOpenHref,
} from './obligationProposalNotificationUtils';
/** @param {import('./buildNotificationContext').NotificationDisplayContext} ctx */
function consultationNotice(ctx, documentId) {
  return documentId ? ctx.resolveConsultationByDocument?.(documentId) : null;
}

/** Deep link to consultation PDF document (related_entity_id = document UUID). */
function consultationDocumentNotificationLinks(n, ctx) {
  const documentId = getEntityId(n);
  const info = consultationNotice(ctx, documentId);
  if (info?.slug && documentId) {
    return [
      {
        key: 'consultation',
        to: getNoticeConsultationDocumentUrl(info.slug, documentId),
        label: info.title || 'Објава',
        linkHint: 'Документ консултације',
      },
    ];
  }
  return [{ key: 'notices', to: getNoticesListUrl(), label: 'Објаве' }];
}

/** Profile settings link for organization creator notifications. */
function profileOrganizationSettingsLinks(n) {
  const entityId = getEntityId(n);
  const params = new URLSearchParams({ tab: 'settings' });
  if (entityId) params.set('organizationId', entityId);
  return [{ key: 'settings', to: `/profil?${params.toString()}`, label: 'Подешавања' }];
}

/** Canonical type for obligation moderator assignment notifications. */
const MODERATOR_ASSIGNED = 'MODERATOR_ASSIGNED_TO_OBLIGATION';

function getType(notification) {
  return (
    notification?.notification_type ||
    notification?.type ||
    notification?.notificationType ||
    notification?.event_type ||
    notification?.eventType ||
    ''
  );
}

function getEntityId(notification) {
  const id =
    notification?.related_entity_id ??
    notification?.relatedEntityId ??
    notification?.entity_id ??
    notification?.entityId;
  return id != null && id !== '' ? String(id) : null;
}

function getApiNotificationBody(notification) {
  return String(
    notification?.text ??
      notification?.body ??
      notification?.message ??
      notification?.description ??
      notification?.content ??
      ''
  ).trim();
}

function getApiNotificationTitle(notification) {
  return String(notification?.title ?? notification?.subject ?? '').trim();
}

function isKnownNotificationType(type) {
  return !!type && Object.prototype.hasOwnProperty.call(NOTIFICATION_DEFS, type);
}

function orgName(ctx, entityId) {
  const org = ctx.resolveOrganization(entityId);
  return org?.name?.trim() || null;
}

function obligationName(ctx, entityId) {
  const obl = ctx.resolveObligation(entityId);
  return obl?.name?.trim() || null;
}

function adminObligationLink(entityId, obl) {
  if (!entityId) {
    return {
      key: 'admin-ap',
      to: APP_PATHS.ADMIN.ACTION_PLAN.LIST.replace(/\/\*$/, ''),
      label: 'Акциони планови',
    };
  }
  const apId = obl?.action_plan_id ?? obl?._apId ?? obl?.actionPlanId;
  return {
    key: 'admin-obl',
    to: getAdminObligationPath(entityId, apId),
    label: 'Преглед',
    linkHint: 'Преглед обавезе',
  };
}

/** Admin panel vs profil — admin uvek na /admin/obaveza/{id}. */
function resolveObligationNotificationLink(entityId, obl, ctx) {
  if (ctx.isAdmin) {
    return adminObligationLink(entityId, obl);
  }
  return buildProfileObligationNotificationLink(entityId, obl);
}

const ORG_ACCESS_TYPES = new Set([
  'ACCEPTED_IN_ORGANIZATION',
  'REJECTED_IN_ORGANIZATION',
  'PROMOTED_TO_ORGANIZATION_LEAD',
]);

/** @type {Record<string, object>} */
const NOTIFICATION_DEFS = {
  ACCEPTED_IN_ORGANIZATION: {
    label: 'Одобрен приступ организацији',
    triggerHint: 'Одобрен је ваш захтев за приступ организацији',
    getContext(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name
        ? `${name}: одобрен је приступ организацији.`
        : 'Одобрен је приступ организацији.';
    },
    getLinks() {
      return [{ key: 'settings', to: '/profil?tab=settings', label: 'Подешавања' }];
    },
    searchParts(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name ? [name] : [];
    },
  },

  REJECTED_IN_ORGANIZATION: {
    label: 'Није прихваћен приступ организацији',
    triggerHint: 'Ваш захтев за приступ организацији није прихваћен',
    getContext(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name
        ? `${name}: приступ није прихваћен.`
        : 'Приступ организацији није прихваћен.';
    },
    getLinks() {
      return [{ key: 'settings', to: '/profil?tab=settings', label: 'Подешавања' }];
    },
    searchParts(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name ? [name] : [];
    },
  },

  PROMOTED_TO_ORGANIZATION_LEAD: {
    label: 'Промовисан у руководиоца',
    triggerHint: 'Додељена вам је улога руководиоца организације',
    getContext(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name
        ? `${name}: промовисани сте у руководиоца.`
        : 'Промовисани сте у руководиоца организације.';
    },
    getLinks() {
      return [{ key: 'settings', to: '/profil?tab=settings', label: 'Подешавања' }];
    },
    searchParts(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name ? [name] : [];
    },
  },

  [MODERATOR_ASSIGNED]: {
    label: 'Додељена улога модератора',
    triggerHint: 'Администратор вас је доделио као модератора обавезе',
    showModeratorActions: true,
    getContext(n, ctx) {
      const entityId = getEntityId(n);
      const obl = ctx.resolveObligation(entityId);
      const name = obligationName(ctx, entityId) || 'Обавеза у припреми';
      return buildModeratorAssignedNotificationContext(name, obl?.status);
    },
    getLinks(n, ctx) {
      const entityId = getEntityId(n);
      const obl = ctx.resolveObligation(entityId);
      return [buildModeratorObligationNotificationLink(entityId, obl)];
    },
    searchParts(n, ctx) {
      const name = obligationName(ctx, getEntityId(n));
      return name ? [name] : [];
    },
  },

  CSO_CREATED_PENDING_APPROVAL: {
    label: 'Нова ОЦД на чекању',
    triggerHint: 'Нова пријава организације цивилног друштва чека одобрење',
    getContext(n, ctx) {
      const entityId = getEntityId(n);
      const name = orgName(ctx, entityId);
      if (name) {
        return `ОЦД „${name}” чека администраторско одобрење.`;
      }
      return 'Нова ОЦД чека администраторско одобрење.';
    },
    getLinks(n, ctx) {
      const entityId = getEntityId(n);
      if (entityId) {
        return [
          {
            key: 'cso-edit',
            to: `${APP_PATHS.ADMIN.ROOT}/organizacije/edit/${entityId}`,
            label: 'Преглед',
            linkHint: 'Пријава ОЦД',
          },
        ];
      }
      return [
        {
          key: 'cso-registry',
          to: APP_PATHS.ADMIN.CSO.ROOT.replace(/\/\*$/, ''),
          label: 'Регистар',
        },
      ];
    },
    searchParts(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name ? [name, 'ОЦД', 'одобрење'] : ['ОЦД', 'одобрење'];
    },
  },

  PUBLIC_BODY_CREATED_PENDING_APPROVAL: {
    label: 'Нова институција на чекању',
    triggerHint: 'Нова пријава институције (јавног органа) чека одобрење',
    getContext(n, ctx) {
      const entityId = getEntityId(n);
      const name = orgName(ctx, entityId);
      if (name) {
        return `Институција „${name}” чека администраторско одобрење.`;
      }
      return 'Нова институција чека администраторско одобрење.';
    },
    getLinks(n, ctx) {
      const entityId = getEntityId(n);
      if (entityId) {
        return [
          {
            key: 'institution-edit',
            to: `${APP_PATHS.ADMIN.ROOT}/institucija/${entityId}`,
            label: 'Преглед',
            linkHint: 'Институција',
          },
        ];
      }
      return [
        {
          key: 'institutions-registry',
          to: APP_PATHS.ADMIN.INSTITUTIONS.ROOT.replace(/\/\*$/, ''),
          label: 'Регистар',
        },
      ];
    },
    searchParts(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name ? [name, 'институција', 'одобрење'] : ['институција', 'одобрење'];
    },
  },

  CSO_SUBMITTED_FOR_APPROVAL: {
    label: 'ОЦД на одобрењу',
    triggerHint: 'Послата је ОЦД на одобрење',
    getContext(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      if (name) {
        return `ОЦД „${name}” је послата на одобрење.`;
      }
      return 'ОЦД је послата на одобрење.';
    },
    getLinks: profileOrganizationSettingsLinks,
    searchParts(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name ? [name, 'ОЦД', 'одобрење'] : ['ОЦД', 'одобрење'];
    },
  },

  PUBLIC_BODY_SUBMITTED_FOR_APPROVAL: {
    label: 'Институција на одобрењу',
    triggerHint: 'Послата је институција на одобрење',
    getContext(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      if (name) {
        return `Институција „${name}” је послата на одобрење.`;
      }
      return 'Институција је послата на одобрење.';
    },
    getLinks: profileOrganizationSettingsLinks,
    searchParts(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name ? [name, 'институција', 'одобрење'] : ['институција', 'одобрење'];
    },
  },

  CSO_APPROVED: {
    label: 'Одобрена ОЦД',
    triggerHint: 'ОЦД је одобрена',
    getContext(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      if (name) {
        return `ОЦД „${name}” је одобрена.`;
      }
      return 'ОЦД је одобрена.';
    },
    getLinks: profileOrganizationSettingsLinks,
    searchParts(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name ? [name, 'ОЦД', 'одобрена'] : ['ОЦД', 'одобрена'];
    },
  },

  PUBLIC_BODY_APPROVED: {
    label: 'Одобрена институција',
    triggerHint: 'Институција је одобрена',
    getContext(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      if (name) {
        return `Институција „${name}” је одобрена.`;
      }
      return 'Институција је одобрена.';
    },
    getLinks: profileOrganizationSettingsLinks,
    searchParts(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name ? [name, 'институција', 'одобрена'] : ['институција', 'одобрена'];
    },
  },

  ORGANIZATION_REJECTED: {
    label: 'Одбијена организација',
    triggerHint: 'Пријава организације није одобрена',
    getContext(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      if (name) {
        return `Организација „${name}” није одобрена.`;
      }
      return 'Пријава организације није одобрена.';
    },
    getLinks: profileOrganizationSettingsLinks,
    searchParts(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name ? [name, 'организација', 'одбијена'] : ['организација', 'одбијена'];
    },
  },

  MODERATOR_REJECTED_OBLIGATION: {
    label: 'Модератор је одбио обавезу',
    triggerHint: 'Модератор је одбио доделу на обавези',
    getContext(n, ctx) {
      const entityId = getEntityId(n);
      const name = obligationName(ctx, entityId);
      return name
        ? `${name}: модератор је одбио доделу.`
        : 'Модератор је одбио доделу на обавези.';
    },
    getLinks(n, ctx) {
      const entityId = getEntityId(n);
      const obl = ctx.resolveObligation(entityId);
      return [adminObligationLink(entityId, obl)];
    },
    searchParts(n, ctx) {
      const name = obligationName(ctx, getEntityId(n));
      return name ? [name, 'модератор'] : ['модератор'];
    },
  },

  OBLIGATION_CREATED_PENDING_REVIEW: {
    label: 'Нова обавеза за преглед',
    triggerHint: 'Креирана је обавеза која чека преглед',
    getContext(n, ctx) {
      const entityId = getEntityId(n);
      const name = obligationName(ctx, entityId);
      return name
        ? `${name}: нова обавеза чека администраторски преглед.`
        : 'Нова обавеза чека администраторски преглед.';
    },
    getLinks(n, ctx) {
      const entityId = getEntityId(n);
      const obl = ctx.resolveObligation(entityId);
      return [adminObligationLink(entityId, obl)];
    },
    searchParts(n, ctx) {
      const name = obligationName(ctx, getEntityId(n));
      return name ? [name] : [];
    },
  },

  OBLIGATION_SUBMITTED_FOR_REVIEW: {
    label: 'Обавеза на прегледу',
    triggerHint: 'Послата је обавеза на преглед',
    getContext(n, ctx) {
      const name = obligationName(ctx, getEntityId(n));
      return name
        ? `${name}: обавеза је послата на преглед.`
        : 'Обавеза је послата на преглед.';
    },
    getLinks(n, ctx) {
      const entityId = getEntityId(n);
      const obl = ctx.resolveObligation(entityId);
      return [buildProfileObligationNotificationLink(entityId, obl)];
    },
    searchParts(n, ctx) {
      const name = obligationName(ctx, getEntityId(n));
      return name ? [name, 'преглед'] : ['преглед'];
    },
  },

  IMPLEMENTATION_DELAY_REPORTED: {
    label: 'Пријављено значајно кашњење',
    triggerHint: 'За имплементацију је пријављен статус значајног кашњења',
    getContext(n, ctx) {
      const entityId = getEntityId(n);
      const name = obligationName(ctx, entityId);
      return name
        ? `${name}: пријављено је значајно кашњење у спровођењу.`
        : 'Пријављено је значајно кашњење у спровођењу обавезе.';
    },
    getLinks(n, ctx) {
      const entityId = getEntityId(n);
      const obl = ctx.resolveObligation(entityId);
      const apId = obl?.action_plan_id ?? obl?._apId;
      if (entityId) {
        return [
          {
            key: 'report',
            to: getAdminObligationReportPath(entityId, apId),
            label: 'Извештај',
            linkHint: 'Извештај о кашњењу',
          },
          adminObligationLink(entityId, obl),
        ];
      }
      return [adminObligationLink(entityId, obl)];
    },
    searchParts(n, ctx) {
      const name = obligationName(ctx, getEntityId(n));
      return name ? [name, 'кашњење'] : ['кашњење'];
    },
  },

  ORGANIZATION_ACCESS_REQUESTED: {
    label: 'Захтев за приступ организацији',
    triggerHint: 'Корисник је затражио приступ вашој организацији',
    getContext(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name
        ? `${name}: нови захтев за приступ — прегледајте кориснике организације.`
        : 'Нови захтев за приступ организацији — прегледајте листу корисника.';
    },
    getLinks(n) {
      const entityId = getEntityId(n);
      const params = new URLSearchParams({ tab: 'users' });
      if (entityId) params.set('organizationId', entityId);
      return [{ key: 'users', to: `/profil?${params.toString()}`, label: 'Корисници' }];
    },
    searchParts(n, ctx) {
      const name = orgName(ctx, getEntityId(n));
      return name ? [name, 'приступ'] : ['приступ'];
    },
  },

  OBLIGATION_STATUS_CHANGED: {
    label: 'Промена статуса обавезе',
    triggerHint: 'Статус обавезе је промењен',
    getContext(n, ctx) {
      const entityId = getEntityId(n);
      const name = obligationName(ctx, entityId);
      return name
        ? `${name}: статус обавезе је промењен.`
        : 'Статус обавезе је промењен.';
    },
    getLinks(n, ctx) {
      const entityId = getEntityId(n);
      const obl = ctx.resolveObligation(entityId);
      return [resolveObligationNotificationLink(entityId, obl, ctx)];
    },
    searchParts(n, ctx) {
      const name = obligationName(ctx, getEntityId(n));
      return name ? [name, 'статус'] : ['статус'];
    },
  },

  OBLIGATION_ACTIVITY_STATUS_CHANGED: {
    label: 'Промена статуса активности обавезе',
    triggerHint: 'Статус активности обавезе је промењен',
    getContext(n, ctx) {
      const entityId = getEntityId(n);
      const name = obligationName(ctx, entityId);
      return name
        ? `${name}: статус активности је промењен.`
        : 'Статус активности обавезе је промењен.';
    },
    getLinks(n, ctx) {
      const entityId = getEntityId(n);
      const obl = ctx.resolveObligation(entityId);
      return [resolveObligationNotificationLink(entityId, obl, ctx)];
    },
    searchParts(n, ctx) {
      const name = obligationName(ctx, getEntityId(n));
      return name ? [name, 'активност', 'статус'] : ['активност', 'статус'];
    },
  },

  ACTION_PLAN_STATUS_CHANGED: {
    label: 'Промена статуса акционог плана',
    triggerHint: 'Статус акционог плана је промењен',
    getContext() {
      return 'Статус акционог плана је промењен.';
    },
    getLinks(n, ctx) {
      const entityId = getEntityId(n);
      if (entityId) {
        return [
          {
            key: 'action-plan',
            to: ctx.isAdmin
              ? getAdminActionPlanDetailPath(entityId)
              : getHomeActionPlanCtaHref(entityId, {
                  draftActionPlanId: ctx.draftActionPlanId,
                  implementationActionPlanId: ctx.implementationActionPlanId,
                  completedActionPlanIds: ctx.completedActionPlanIds,
                }),
            label: 'Преглед',
            linkHint: 'Акциони план',
          },
        ];
      }
      if (ctx.isAdmin) {
        return [
          {
            key: 'admin-ap',
            to: APP_PATHS.ADMIN.ACTION_PLAN.LIST.replace(/\/\*$/, ''),
            label: 'Акциони планови',
          },
        ];
      }
      return [
        {
          key: 'home-ap-cta',
          to: '/',
          label: 'Почетна',
        },
      ];
    },
    searchParts() {
      return ['акциони план', 'статус'];
    },
  },

  OBLIGATION_PROPOSAL_FINALIZED: {
    label: 'Предлог финализован',
    triggerHint: 'Предлог обавезе чека админ финализацију',
    getContext(n, ctx) {
      const entityId = getEntityId(n);
      const name = obligationName(ctx, entityId);
      return name
        ? `${name}: предлог је финализован и чека администраторску обраду.`
        : 'Предлог обавезе је финализован и чека администраторску обраду.';
    },
    getLinks(n, ctx) {
      const entityId = getEntityId(n);
      const obl = ctx.resolveObligation(entityId);
      return [resolveObligationNotificationLink(entityId, obl, ctx)];
    },
    searchParts(n, ctx) {
      const name = obligationName(ctx, getEntityId(n));
      return name ? [name, 'финализован'] : ['финализован'];
    },
  },

  OBLIGATION_PROPOSAL_UPDATED: {
    label: 'Ажуриран предлог обавезе',
    triggerHint: 'Учесник није сагласан са предлогом и доставио је свој',
    getContext(n, ctx) {
      const proposalId = getEntityId(n);
      const meta = ctx.resolveObligationProposal?.(proposalId);
      const obligationId = meta?.obligation_id ?? null;
      const name = obligationId ? obligationName(ctx, obligationId) : null;
      return name
        ? `${name}: учесник није сагласан са предлогом и доставио је свој предлог.`
        : 'Учесник није сагласан са предлогом обавезе и доставио је свој предлог.';
    },
    getLinks(n, ctx) {
      const proposalId = getEntityId(n);
      if (!proposalId) {
        return [{ key: 'obaveze', to: '/profil?tab=obaveze', label: 'Обавезе' }];
      }

      const meta = ctx.resolveObligationProposal?.(proposalId);
      const obligationId = meta?.obligation_id ?? null;
      const obl = obligationId ? ctx.resolveObligation(obligationId) : null;
      const actionPlanId =
        meta?.action_plan_id ??
        obl?.action_plan_id ??
        obl?._apId ??
        obl?.actionPlanId ??
        ctx.draftActionPlanId ??
        null;
      const linkQuery = {
        actionPlanId,
        formTab: meta?.formTab ?? null,
        fieldKey: meta?.fieldKey ?? null,
      };
      const href = obligationId
        ? buildProfileObligationProposalFocusHref({
            obligationId,
            actionPlanId,
            proposalId,
            formTab: linkQuery.formTab,
            fieldKey: linkQuery.fieldKey,
          })
        : buildProfileObligationProposalOpenHref(proposalId, linkQuery);

      return [{
        key: 'obl-profile',
        to: href,
        label: 'Преглед',
        linkHint: 'Преглед обавезе',
      }];
    },
    searchParts(n, ctx) {
      const proposalId = getEntityId(n);
      const meta = ctx.resolveObligationProposal?.(proposalId);
      const obligationId = meta?.obligation_id ?? null;
      const name = obligationId ? obligationName(ctx, obligationId) : null;
      return name ? [name, 'предлог', 'ажуриран'] : ['предлог', 'ажуриран'];
    },
  },

  DOCUMENT_COMMENT_STATUS_CHANGED: {
    label: 'Промена статуса коментара',
    triggerHint: 'Статус је промењен на коментару у јавној консултацији',
    getContext(n, ctx) {
      const documentId = getEntityId(n);
      const info = consultationNotice(ctx, documentId);
      if (info?.title) {
        return `„${info.title}”: промењен је статус коментара на документу у јавној консултацији.`;
      }
      return 'Промењен је статус коментара на документу у јавној консултацији.';
    },
    getLinks: consultationDocumentNotificationLinks,
    searchParts(n, ctx) {
      const info = consultationNotice(ctx, getEntityId(n));
      return info?.title
        ? [info.title, 'консултација', 'коментар', 'статус']
        : ['консултација', 'коментар', 'статус'];
    },
  },

  DOCUMENT_COMMENT_SUBMITTED_FOR_REVIEW: {
    label: 'Коментар на прегледу',
    triggerHint: 'Послат је коментар на преглед у јавној консултацији',
    getContext(n, ctx) {
      const documentId = getEntityId(n);
      const info = consultationNotice(ctx, documentId);
      if (info?.title) {
        return `„${info.title}”: послат је коментар на преглед на документу у јавној консултацији.`;
      }
      return 'Послат је коментар на преглед на документу у јавној консултацији.';
    },
    getLinks: consultationDocumentNotificationLinks,
    searchParts(n, ctx) {
      const info = consultationNotice(ctx, getEntityId(n));
      return info?.title
        ? [info.title, 'консултација', 'коментар', 'преглед']
        : ['консултација', 'коментар', 'преглед'];
    },
  },

  CONSULTATION_COMMENT_CREATED: {
    label: 'Нови коментар на консултацији',
    triggerHint: 'Додат је коментар на документу у јавној консултацији',
    getContext(n, ctx) {
      const documentId = getEntityId(n);
      const info = consultationNotice(ctx, documentId);
      if (info?.title) {
        return `„${info.title}”: додат је нови коментар на документу у јавној консултацији.`;
      }
      return 'Додат је нови коментар на документу у јавној консултацији.';
    },
    getLinks: consultationDocumentNotificationLinks,
    searchParts(n, ctx) {
      const info = consultationNotice(ctx, getEntityId(n));
      return info?.title ? [info.title, 'консултација', 'коментар'] : ['консултација', 'коментар'];
    },
  },

  MODERATOR_ACTION_REQUIRED: {
    label: 'Потребна акција модератора',
    triggerHint: 'Нови коментар на консултацији захтева преглед модератора',
    getContext(n, ctx) {
      const documentId = getEntityId(n);
      const info = consultationNotice(ctx, documentId);
      if (info?.title) {
        return `„${info.title}”: нови коментар на консултацији захтева ваш преглед.`;
      }
      return 'Нови коментар на консултацији захтева ваш преглед.';
    },
    getLinks: consultationDocumentNotificationLinks,
    searchParts(n, ctx) {
      const info = consultationNotice(ctx, getEntityId(n));
      return info?.title
        ? [info.title, 'консултација', 'модератор']
        : ['консултација', 'модератор'];
    },
  },
};

// Backward-compatible aliases
NOTIFICATION_DEFS.ORGANIZATION_CREATED_PENDING_APPROVAL =
  NOTIFICATION_DEFS.CSO_CREATED_PENDING_APPROVAL;
NOTIFICATION_DEFS.ORGANIZATION_SUBMITTED_FOR_APPROVAL =
  NOTIFICATION_DEFS.CSO_SUBMITTED_FOR_APPROVAL;
NOTIFICATION_DEFS.ORGANIZATION_APPROVED =
  NOTIFICATION_DEFS.CSO_APPROVED;

const FALLBACK_DEF = {
  label: 'Обавештење',
  triggerHint: 'Нови догађај у систему',
  getContext(n) {
    const apiBody = getApiNotificationBody(n);
    if (apiBody) return apiBody;
    const entityId = getEntityId(n);
    const type = getType(n);
    if (entityId && type) return `Догађај „${type}” (повезани ID: ${entityId}).`;
    if (type) return `Догађај типа „${type}”.`;
    return 'Имате ново обавештење.';
  },
  getLinks(n, ctx) {
    if (ctx.isAdmin) {
      return [{ key: 'admin', to: APP_PATHS.ADMIN.ROOT, label: 'Админ' }];
    }
    return [{ key: 'notifications', to: '/profil?tab=notifications', label: 'Обавештења' }];
  },
  searchParts(n) {
    const type = getType(n);
    return type ? [type] : [];
  },
};

function getDef(type) {
  if (!type) return FALLBACK_DEF;
  return NOTIFICATION_DEFS[type] || FALLBACK_DEF;
}

/**
 * @param {string} type
 * @returns {string}
 */
export function getNotificationTypeLabel(type) {
  return getDef(type).label || type || FALLBACK_DEF.label;
}

/**
 * @param {string} type
 * @returns {string|null}
 */
export function getNotificationTriggerHint(type) {
  const hint = getDef(type).triggerHint;
  return hint || null;
}

/**
 * @param {object} notification
 * @param {import('./buildNotificationContext').NotificationDisplayContext} ctx
 * @returns {{
 *   type: string,
 *   label: string,
 *   triggerHint: string|null,
 *   context: string|null,
 *   links: Array<{ key: string, to: string, label: string, linkHint?: string }>,
 *   showModeratorActions: boolean,
 *   searchParts: string[],
 * }}
 */
export function resolveNotificationDisplay(notification, ctx) {
  const type = getType(notification);
  const def = getDef(type);
  const entityId = getEntityId(notification);
  const useApiPreview = !isKnownNotificationType(type);
  const apiTitle = getApiNotificationTitle(notification);
  const apiBody = getApiNotificationBody(notification);

  const contextRaw = def.getContext?.(notification, ctx);
  let context = contextRaw ? String(contextRaw).trim() : null;
  if (useApiPreview && apiBody) {
    context = apiBody;
  }

  let links = def.getLinks?.(notification, ctx) ?? [];
  if (!def.noLinks && (!Array.isArray(links) || links.length === 0)) {
    links = ctx.isAdmin
      ? [{ key: 'admin', to: APP_PATHS.ADMIN.ROOT, label: 'Админ' }]
      : [{ key: 'notifications', to: '/profil?tab=notifications', label: 'Обавештења' }];
  }

  let label = getNotificationTypeLabel(type);
  if (useApiPreview && apiTitle) {
    label = apiTitle;
  }

  let triggerHint = def.triggerHint || null;
  if (useApiPreview && apiTitle && apiBody && triggerHint === FALLBACK_DEF.triggerHint) {
    triggerHint = null;
  }

  const searchRaw = def.searchParts?.(notification, ctx) ?? [];
  const searchParts = [
    label,
    triggerHint,
    context,
    entityId,
    apiTitle,
    apiBody,
    ...searchRaw,
  ]
    .filter(Boolean)
    .map((p) => String(p).toLowerCase());

  return {
    type,
    label,
    triggerHint,
    context,
    links: links.map((link, i) => ({
      key: link.key || `link-${i}`,
      to: link.to,
      label: link.label,
      linkHint: link.linkHint,
    })),
    showModeratorActions: !!def.showModeratorActions,
    searchParts,
  };
}

export function notificationMatchesSearchQuery(notification, queryLower, ctx) {
  if (!queryLower) return true;
  const { searchParts } = resolveNotificationDisplay(notification, ctx);
  return searchParts.some((p) => p.includes(queryLower));
}

export { ORG_ACCESS_TYPES, MODERATOR_ASSIGNED, getType, getEntityId };
